import type { DropDownItem } from '@/components/common/DropDown.vue';
import { computed, reactive } from 'vue';
import type {
  DefaultReviewerSelectorAction,
  DefaultReviewerSelectorMode,
  Reviewer,
} from '../types';
import { createDefaultReviewerSelectorStore } from './createDefaultReviewerSelectorStore';

export interface UseDefaultReviewerSelectorLogicProps {
  allReviewers: Reviewer[];
  defaultReviewers?: Reviewer[];
  initialSelectedIds?: string[];
  isSaving: boolean;
  mode?: DefaultReviewerSelectorMode;
}

export type DisplayedReviewer = Reviewer & { isEligible: boolean };

export const useDefaultReviewerSelectorLogic = (
  props: UseDefaultReviewerSelectorLogicProps,
  emit: (event: 'action', payload: DefaultReviewerSelectorAction) => void,
) => {
  const mode = computed(() => props.mode ?? 'read-only');

  const { data: store, dispatch } = createDefaultReviewerSelectorStore(
    () => props.initialSelectedIds,
  );

  const eligibleIds = computed(
    () => new Set(props.allReviewers.map((r) => r.id)),
  );

  const reviewerById = computed(() => {
    const map = new Map<string, Reviewer>();
    for (const r of [
      ...props.allReviewers,
      ...(props.defaultReviewers ?? []),
    ]) {
      map.set(r.id, r);
    }
    return map;
  });

  const hydrate = (ids: string[]): DisplayedReviewer[] =>
    ids
      .map((id) => reviewerById.value.get(id))
      .filter((r): r is Reviewer => Boolean(r))
      .map((r) => ({ ...r, isEligible: eligibleIds.value.has(r.id) }));

  const selectedReviewers = computed(() => hydrate(store.committedIds));

  const filteredReviewers = computed(() => {
    const q = store.query.toLowerCase();
    const draft = hydrate(store.draftIds);
    if (!q) return draft;
    return draft.filter(
      (r) =>
        r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
    );
  });

  const dropdownItems = computed<DropDownItem[]>(() =>
    props.allReviewers
      .filter((r) => !store.draftIds.includes(r.id))
      .map((r) => ({ value: r.id, text: r.name })),
  );

  const canSave = computed(
    () =>
      !props.isSaving &&
      (store.draftIds.length !== store.committedIds.length ||
        store.draftIds.some((id, i) => id !== store.committedIds[i])),
  );

  const state = reactive({
    get isOpen() {
      return store.isOpen;
    },
    get isSaving() {
      return props.isSaving;
    },
    get query() {
      return store.query;
    },
    get reviewers() {
      return filteredReviewers.value;
    },
    get selectedReviewers() {
      return selectedReviewers.value;
    },
    get dropdownValue() {
      return store.dropdownValue;
    },
    get dropdownItems() {
      return dropdownItems.value;
    },
    get canSave() {
      return canSave.value;
    },
    show: reactive({
      get addReviewerRow() {
        return mode.value === 'edit';
      },
      get orderButtons() {
        return mode.value === 'edit';
      },
      get removeButton() {
        return mode.value === 'edit';
      },
      get footer() {
        return mode.value === 'edit';
      },
    }),
  });

  const action = {
    open: () => dispatch({ type: 'open' }),
    close: () => dispatch({ type: 'close' }),
    search: (value: string) => dispatch({ type: 'search', value }),
    deselect: (id: string) => dispatch({ type: 'deselect', id }),
    addReviewer: (id: string) => dispatch({ type: 'add', id }),
    moveUp: (id: string) => dispatch({ type: 'move-up', id }),
    moveDown: (id: string) => dispatch({ type: 'move-down', id }),
    cancel: () => dispatch({ type: 'close' }),
    save: () => {
      dispatch({ type: 'commit' });
      emit('action', { type: 'save', payload: store.committedIds });
    },
  };

  return { state, action };
};
